/**
 * useOrgContext Hook
 *
 * Provides organization context for multi-tenant data isolation.
 * Resolution order:
 *   1. Cross-app token / sessionStorage context (resolveContext — set when
 *      the OR app is launched from Forge/Surgery with ?token=)
 *   2. Authenticated session lookup via org_members (getCurrentOrgId)
 *   3. Dev fallback org — local development only
 *
 * org_id is null while resolving (or when unresolvable in production) —
 * callers must guard before using it in queries/inserts.
 */

import { useQuery } from '@tanstack/react-query';
import { resolveContext, getCurrentOrgId } from '@/lib/crossApp';

const DEV_ORG_ID = '00000000-0000-0000-0000-000000000001';

interface OrgContext {
    org_id: string | null;
    isDevMode: boolean;
    loading: boolean;
}

async function resolveOrgContext(): Promise<{ org_id: string; isDevMode: boolean }> {
    // 1. Cross-app token / stored encounter context
    const ctx = await resolveContext();
    if (ctx?.org_id) return { org_id: ctx.org_id, isDevMode: false };

    // 2. Logged-in session → org_members lookup
    const orgId = await getCurrentOrgId();
    if (orgId) return { org_id: orgId, isDevMode: false };

    // 3. Local development fallback
    if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[Dev Mode] Using hardcoded dev org_id. This is normal for local development.');
        return { org_id: DEV_ORG_ID, isDevMode: true };
    }

    throw new Error(
        'No organization context available. Sign in, or launch PatientTrac Ambulatory from PatientTracForge.'
    );
}

export function useOrgContext(): OrgContext {
    const { data, isLoading } = useQuery({
        queryKey: ['org-context'],
        queryFn: resolveOrgContext,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: 1
    });

    return {
        org_id: data?.org_id ?? null,
        isDevMode: data?.isDevMode ?? false,
        loading: isLoading
    };
}

/** Convenience variant — null until resolved; guard before use. */
export function useOrgId(): string | null {
    return useOrgContext().org_id;
}
